import { useEffect, useState } from 'react';
import {
    Box,
    Grid,
    Typography,
    Card,
    CardContent,
    Chip,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    List,
    ListItem,
    ListItemText,
} from '@mui/material';
import {
    asyncGetQuestions,
    asyncGetAnswers,
} from '../../../../../../services/survey.service';
import Loading from '../../../../../components/general/loadings/loading';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

interface Props {
    surveyId?: string;
}

export const SurveyQuestions = ({ surveyId }: Props) => {
    const [questions, setQuestions] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [answers, setAnswers] = useState<{ [key: string]: any[] }>({});

    useEffect(() => {
        if (surveyId) {
            loadQuestions();
        }
    }, [surveyId]);

    const loadQuestions = async () => {
        if (!surveyId) return;
        setLoading(true);
        try {
            const data = await asyncGetQuestions(surveyId);
            setQuestions(data || []);

            // Cargar respuestas para preguntas que no sean de tipo texto
            const answersData: { [key: string]: any[] } = {};
            for (const question of data || []) {
                if (['single', 'multiple', 'linear'].includes(question.type)) {
                    try {
                        const questionAnswers = await asyncGetAnswers(
                            question.id
                        );
                        answersData[question.id] = questionAnswers || [];
                    } catch (error) {
                        console.error(
                            `Error loading answers for question ${question.id}:`,
                            error
                        );
                        answersData[question.id] = [];
                    }
                }
            }
            setAnswers(answersData);
        } catch (error) {
            console.error('Error loading questions:', error);
        } finally {
            setLoading(false);
        }
    };

    const getTypeLabel = (type: string) => {
        const types: any = {
            text: 'Texto',
            date: 'Fecha',
            single: 'Opción única',
            multiple: 'Opción múltiple',
            linear: 'Escala lineal',
        };
        return types[type] || type;
    };

    if (loading) {
        return <Loading />;
    }

    if (questions.length === 0) {
        return (
            <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                    No hay preguntas registradas para esta encuesta
                </Typography>
            </Box>
        );
    }

    return (
        <Grid container spacing={2}>
            {questions.map((question, index) => (
                <Grid item xs={12} key={question.id}>
                    <Card
                        sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            boxShadow: 1,
                        }}
                    >
                        <CardContent>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 2,
                                }}
                            >
                                <HelpOutlineIcon
                                    sx={{ color: 'primary.main', mt: 0.5 }}
                                />
                                <Box sx={{ flex: 1 }}>
                                    <Typography
                                        variant="subtitle1"
                                        sx={{
                                            fontWeight: 600,
                                            color: 'primary.main',
                                            mb: 1,
                                        }}
                                    >
                                        {index + 1}. {question.description}
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            gap: 1,
                                            flexWrap: 'wrap',
                                        }}
                                    >
                                        <Chip
                                            label={getTypeLabel(question.type)}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                        />
                                        {question.required && (
                                            <Chip
                                                label="Requerida"
                                                size="small"
                                                color="error"
                                                variant="outlined"
                                            />
                                        )}
                                        {question.why && (
                                            <Chip
                                                label="Con ¿Por qué?"
                                                size="small"
                                                color="info"
                                                variant="outlined"
                                            />
                                        )}
                                        <Chip
                                            label={
                                                question.status === 'active'
                                                    ? 'Activa'
                                                    : 'Inactiva'
                                            }
                                            size="small"
                                            color={
                                                question.status === 'active'
                                                    ? 'success'
                                                    : 'default'
                                            }
                                            variant="outlined"
                                        />
                                    </Box>

                                    {/* Accordion para mostrar respuestas */}
                                    {['single', 'multiple', 'linear'].includes(
                                        question.type
                                    ) && (
                                        <Accordion
                                            sx={{
                                                mt: 2,
                                                boxShadow: 'none',
                                                '&:before': {
                                                    display: 'none',
                                                },
                                            }}
                                        >
                                            <AccordionSummary
                                                expandIcon={<ExpandMoreIcon />}
                                                sx={{
                                                    backgroundColor: '#f5f5f5',
                                                    borderRadius: 1,
                                                    minHeight: 40,
                                                    '&.Mui-expanded': {
                                                        minHeight: 40,
                                                    },
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    sx={{ fontWeight: 500 }}
                                                >
                                                    Ver respuestas (
                                                    {answers[question.id]
                                                        ?.length || 0}
                                                    )
                                                </Typography>
                                            </AccordionSummary>
                                            <AccordionDetails sx={{ pt: 2 }}>
                                                {answers[question.id] &&
                                                answers[question.id].length >
                                                    0 ? (
                                                        question.type ===
                                                    'linear' ? (
                                                                <Box>
                                                                    <Typography
                                                                        variant="body2"
                                                                        color="text.secondary"
                                                                    >
                                                                        <strong>
                                                                    Rango:
                                                                        </strong>{' '}
                                                                        {answers[
                                                                            question.id
                                                                        ][0]?.range ||
                                                                    'No definido'}
                                                                    </Typography>
                                                                </Box>
                                                            ) : (
                                                                <List dense>
                                                                    {answers[
                                                                        question.id
                                                                    ].map(
                                                                        (
                                                                            answer: any,
                                                                            idx: number
                                                                        ) => (
                                                                            <ListItem
                                                                                key={
                                                                                    answer.id
                                                                                }
                                                                                sx={{
                                                                                    py: 0.5,
                                                                                }}
                                                                            >
                                                                                <ListItemText
                                                                                    primary={`${
                                                                                        idx +
                                                                                1
                                                                                    }. ${
                                                                                        answer.description
                                                                                    }`}
                                                                                    primaryTypographyProps={{
                                                                                        variant:
                                                                                    'body2',
                                                                                    }}
                                                                                />
                                                                            </ListItem>
                                                                        )
                                                                    )}
                                                                </List>
                                                            )
                                                    ) : (
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                        >
                                                        No hay respuestas
                                                        configuradas
                                                        </Typography>
                                                    )}
                                            </AccordionDetails>
                                        </Accordion>
                                    )}
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            ))}
        </Grid>
    );
};
